/** Paytable overlay screen showing symbol payouts, features, and rules */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { PAY_TABLE } from '../config/PayTable';
import { GameConfig } from '../config/GameConfig';
import { Logger } from '../utils/Logger';

const logger = new Logger('Paytable');

/** Full-screen paytable overlay */
export class Paytable extends Container {
  private _onClose: (() => void) | null = null;

  constructor(designW: number = 1920, designH: number = 1080) {
    super();
    this.eventMode = 'static';
    this.visible = false;

    // Semi-transparent dark background
    const bg = new Graphics();
    bg.rect(0, 0, designW, designH);
    bg.fill({ color: 0x000000, alpha: 0.85 });
    bg.eventMode = 'static';
    bg.on('pointerdown', (e) => e.stopPropagation()); // block clicks through
    this.addChild(bg);

    // Content panel
    const panelW = 900;
    const panelH = 750;
    const panelX = (designW - panelW) / 2;
    const panelY = (designH - panelH) / 2;

    const panel = new Graphics();
    panel.roundRect(panelX, panelY, panelW, panelH, 16);
    panel.fill({ color: 0x111133, alpha: 0.95 });
    panel.stroke({ color: 0x4444aa, width: 3 });
    this.addChild(panel);

    // Title
    const title = new Text({
      text: 'PAYTABLE',
      style: new TextStyle({
        fontFamily: 'Arial Black, Arial',
        fontSize: 36,
        fill: 0xf7b733,
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    title.anchor.set(0.5, 0);
    title.x = designW / 2;
    title.y = panelY + 20;
    this.addChild(title);

    // Symbol payouts table
    let yOffset = panelY + 75;
    this._addSectionTitle('SYMBOL PAYOUTS (coins per way)', designW / 2, yOffset);
    yOffset += 35;

    // Column headers
    const headerStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 14,
      fill: 0xaaaaaa,
    });
    const colX = panelX + 40;
    const colWidths = [120, 80, 80, 80, 80, 80, 80];
    const headers = ['Symbol', '2x', '3x', '4x', '5x', '6x', 'Min'];

    for (let i = 0; i < headers.length; i++) {
      const hdr = new Text({
        text: headers[i],
        style: headerStyle,
      });
      let xPos = colX;
      for (let j = 0; j < i; j++) xPos += colWidths[j];
      hdr.x = xPos;
      hdr.y = yOffset;
      this.addChild(hdr);
    }
    yOffset += 28;

    // Separator line
    const sepLine = new Graphics();
    sepLine.moveTo(panelX + 30, yOffset);
    sepLine.lineTo(panelX + panelW - 30, yOffset);
    sepLine.stroke({ color: 0x444488, width: 1 });
    this.addChild(sepLine);
    yOffset += 8;

    // Symbol rows
    const rowStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 16,
      fill: 0xffffff,
    });

    const symbolNames: Record<string, string> = {
      'PICa': '7s (WILD sub)',
      'PICb': 'Bar Triple',
      'PICc': 'Bar Double',
      'PICd': 'Bar Single',
      'PICe': 'Cherry',
      'A': 'Ace',
      'K': 'King',
      'Q': 'Queen',
      'J': 'Jack',
      'T': 'Ten',
      'SCATTER': 'Scatter',
    };

    for (const entry of PAY_TABLE) {
      const name = symbolNames[entry.symbol] || entry.symbol;

      const values = [
        name,
        entry.payouts[1] > 0 ? String(entry.payouts[1]) : '-',
        entry.payouts[2] > 0 ? String(entry.payouts[2]) : '-',
        entry.payouts[3] > 0 ? String(entry.payouts[3]) : '-',
        entry.payouts[4] > 0 ? String(entry.payouts[4]) : '-',
        entry.payouts[5] > 0 ? String(entry.payouts[5]) : '-',
        `${entry.minStreak}+`,
      ];

      let xPos = colX;
      for (let i = 0; i < values.length; i++) {
        const cell = new Text({
          text: values[i],
          style: i === 0
            ? new TextStyle({ fontFamily: 'Arial', fontSize: 15, fill: 0xffdd88 })
            : rowStyle,
        });
        cell.x = xPos;
        cell.y = yOffset;
        this.addChild(cell);
        xPos += colWidths[i];
      }
      yOffset += 26;
    }

    // Features section
    yOffset += 20;
    this._addSectionTitle('SPECIAL FEATURES', designW / 2, yOffset);
    yOffset += 35;

    const featureStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 15,
      fill: 0xcccccc,
      wordWrap: true,
      wordWrapWidth: panelW - 80,
    });

    const features = [
      'WILD - Appears on reels 2, 3, 4. Substitutes for all symbols except Scatter and Rush.',
      'SCATTER - 3+ Scatters trigger Free Games. Pays independently on any position.',
      'FREE GAMES - 3 Scatters = 8 Free Spins, 4 = 15, 5 = 20. Retrigger possible.',
      'RUSH FEVER - 3+ Rush symbols trigger the Rush Fever bonus with progressive jackpots.',
      'JACKPOT PICK - During Rush Fever, pick diamonds to reveal jackpot prizes.',
      `243 WAYS TO WIN - Wins pay left to right on adjacent reels. ${GameConfig.WAGER_MULTIPLIER}x bet multiplier.`,
    ];

    for (const feat of features) {
      const featText = new Text({ text: feat, style: featureStyle });
      featText.x = panelX + 40;
      featText.y = yOffset;
      this.addChild(featText);
      yOffset += 38;
    }

    // Close button
    const closeBtn = new Container();
    closeBtn.eventMode = 'static';
    closeBtn.cursor = 'pointer';
    closeBtn.x = panelX + panelW - 50;
    closeBtn.y = panelY + 15;

    const closeBg = new Graphics();
    closeBg.circle(0, 0, 20);
    closeBg.fill({ color: 0xcc3333 });
    closeBg.stroke({ color: 0xffffff, width: 2 });
    closeBtn.addChild(closeBg);

    const closeX = new Text({
      text: 'X',
      style: new TextStyle({
        fontFamily: 'Arial Black',
        fontSize: 20,
        fill: 0xffffff,
      }),
    });
    closeX.anchor.set(0.5);
    closeBtn.addChild(closeX);

    closeBtn.on('pointerdown', () => {
      this.hide();
    });
    this.addChild(closeBtn);

    logger.info('Paytable overlay created');
  }

  /** Set callback for close */
  setOnClose(callback: () => void): void {
    this._onClose = callback;
  }

  /** Show the paytable overlay */
  show(): void {
    this.visible = true;
    logger.info('Paytable shown');
  }

  /** Hide the paytable overlay */
  hide(): void {
    this.visible = false;
    if (this._onClose) {
      this._onClose();
    }
    logger.info('Paytable hidden');
  }

  private _addSectionTitle(text: string, x: number, y: number): void {
    const title = new Text({
      text,
      style: new TextStyle({
        fontFamily: 'Arial Black, Arial',
        fontSize: 20,
        fill: 0xf7b733,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    title.anchor.set(0.5, 0);
    title.x = x;
    title.y = y;
    this.addChild(title);
  }
}
